import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, FileText } from "lucide-react";
import { printPolicyPage } from "@/lib/printPolicy";

const handleDownload = (title) => printPolicyPage({ title, contentElementId: "terms-content" });

export default function TermsOfUsePage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("download") === "1") {
      const t = setTimeout(() => handleDownload("Termini e Condizioni - Colucci Assicuratori"), 600);
      return () => clearTimeout(t);
    }
  }, [params]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="terms-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={() => handleDownload("Termini e Condizioni - Colucci Assicuratori")} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2" data-testid="download-terms-btn">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#163321]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#163321]" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-medium text-[#163321]">Termini e Condizioni d'Uso</h1>
              <p className="text-xs text-[#4A5E51] mt-0.5">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>
            </div>
          </div>

          <article id="terms-content" className="prose prose-sm max-w-none text-[#4A5E51] text-sm leading-relaxed space-y-6">
            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">1. Identificazione del Titolare del sito</h2>
              <p>
                Il sito <strong>www.colucciassicuratori.com</strong> è di proprietà di <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong>, con sede legale in Via Armando Diaz 182/L, 80055 Portici (NA),
                Codice Fiscale e Partita IVA <strong>07618011212</strong>, iscritta al RUI (Registro Unico degli Intermediari) con numero <strong>A000464752</strong> soggetta alla vigilanza dell'IVASS (Istituto per la Vigilanza sulle Assicurazioni, <a href="https://www.ivass.it" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">www.ivass.it</a>).
              </p>
              <p>
                L'iscrizione al RUI è verificabile pubblicamente consultando l'elenco degli intermediari iscritti sul sito IVASS.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">2. Oggetto</h2>
              <p>
                I presenti Termini e Condizioni d'Uso ("Termini") disciplinano l'accesso e l'utilizzo del sito da parte di qualsiasi utente ("Utente"). L'utilizzo del sito comporta l'accettazione integrale e incondizionata dei presenti Termini. In caso di disaccordo, l'Utente è tenuto ad astenersi dall'utilizzo del sito.
              </p>
              <p>
                Il sito consente all'Utente di: (i) richiedere informazioni e preventivi assicurativi; (ii) inviare richieste di consulenza; (iii) consultare il catalogo pubblico di noleggio a lungo termine dei partner Arval e Ayvens; (iv) prenotare consulenze a pagamento; (v) inviare segnalazioni whistleblowing ex D.Lgs. 24/2023.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">3. Natura dei servizi e distribuzione assicurativa</h2>
              <p>
                Colucci Assicuratori svolge attività di <strong>distribuzione assicurativa</strong> ai sensi del D.Lgs. 209/2005 (Codice delle Assicurazioni Private) e del Regolamento IVASS n. 40/2018. Le informazioni pubblicate sul sito hanno natura <strong>promozionale e preliminare</strong> e non costituiscono offerta di contratto assicurativo ai sensi dell'art. 1336 c.c.
              </p>
              <p>
                I preventivi ricevuti attraverso i questionari online sono <strong>proposte indicative non vincolanti</strong>. Il contratto di assicurazione si perfeziona unicamente con la sottoscrizione della polizza conforme alla normativa IVASS, previa consegna dei documenti precontrattuali obbligatori (Modulo Unico Precontrattuale MUP — Allegati 3, 4, 4-bis, 4-ter del Reg. IVASS 40/2018), DIP / IPID / IBIP / KID a seconda del ramo.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">4. Catalogo Noleggio Lungo Termine</h2>
              <p>
                Le offerte di noleggio lungo termine pubblicate sul sito provengono da fonte pubblica dei partner <strong>Arval Italia S.p.A.</strong> (<a href="https://www.arval.it" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">arval.it</a>) e <strong>Ayvens S.p.A.</strong> (<a href="https://noleggio.ayvens.com" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">ayvens.com</a>) e sono aggiornate periodicamente in automatico.
              </p>
              <p>
                <strong>Disclaimer prezzi:</strong> i prezzi, le durate e le condizioni commerciali sono quelli pubblicati dai rispettivi provider al momento dell'ultimo aggiornamento. Colucci Assicuratori non risponde di eventuali variazioni o indisponibilità sopravvenute. Le condizioni definitive sono stabilite direttamente dal provider in fase di stipula.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">5. Obblighi dell'Utente</h2>
              <p>L'Utente si impegna a:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fornire informazioni veritiere, complete e aggiornate nei form di richiesta preventivo e prenotazione consulenza.</li>
                <li>Non utilizzare il sito per finalità illecite, fraudolente, diffamatorie o in violazione di diritti di terzi.</li>
                <li>Non interferire con l'infrastruttura tecnica del sito (attacchi DoS, scraping massivo, tentativi di accesso non autorizzato, iniezione di codice).</li>
                <li>Non inviare dati personali di terzi senza averne titolo (autorizzazione espressa o base giuridica valida).</li>
              </ul>
              <p>
                <strong>Avvertenza art. 1892 c.c.:</strong> le dichiarazioni inesatte o reticenti rese in fase di richiesta preventivo — se poi confermate in sede di stipula — possono determinare l'annullabilità del contratto e la perdita del diritto all'indennizzo in caso di sinistro.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">6. Proprietà intellettuale</h2>
              <p>
                Tutti i contenuti del sito (testi, loghi, marchi, immagini, grafica, codice, database) sono di proprietà esclusiva di Colucci Assicuratori o concessi in licenza e tutelati dalle leggi italiane ed europee sul diritto d'autore (L. 633/1941) e sulla proprietà industriale (D.Lgs. 30/2005). Ogni riproduzione, distribuzione, modifica o utilizzo non autorizzato è vietato.
              </p>
              <p>
                Le immagini e i loghi dei veicoli pubblicati nella sezione Noleggio appartengono ai rispettivi produttori automobilistici e ai partner fornitori (Arval, Ayvens); sono utilizzate in conformità alle condizioni d'uso dei rispettivi siti pubblici.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">7. Limitazione di responsabilità</h2>
              <p>
                Colucci Assicuratori impiega ogni ragionevole diligenza per garantire l'esattezza delle informazioni e la disponibilità del servizio, senza tuttavia offrire garanzie di assenza di errori, interruzioni o malfunzionamenti. È esclusa qualsiasi responsabilità per:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Danni indiretti, lucro cessante, perdita di dati non dipendenti da dolo o colpa grave.</li>
                <li>Decisioni assunte dall'Utente sulla base di informazioni preliminari senza consulenza professionale.</li>
                <li>Inesattezze nel catalogo NLT o nei contenuti AI-generati (es. sezione Notizie Assicurative), che hanno natura divulgativa e non consulenziale.</li>
                <li>Interruzioni tecniche dovute a cause di forza maggiore o a terzi fornitori (hosting, CDN, provider esterni).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">8. Privacy e cookie</h2>
              <p>
                Il trattamento dei dati personali è disciplinato dalla <Link to="/privacy" className="text-[#163321] underline">Privacy Policy</Link> e dalla <Link to="/cookie-policy" className="text-[#163321] underline">Cookie Policy</Link>, che costituiscono parte integrante dei presenti Termini.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">9. Whistleblowing</h2>
              <p>
                Il sito mette a disposizione un canale di <Link to="/whistleblowing" className="text-[#163321] underline">segnalazione interna</Link> conforme al <strong>D.Lgs. 24/2023</strong>. Il segnalante è tutelato da misure di riservatezza e divieto di ritorsione. Le segnalazioni sono trattate nel rispetto della procedura interna disponibile in apposita pagina.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">10. Reclami</h2>
              <p>
                In caso di reclamo relativo al comportamento dell'intermediario assicurativo, l'Utente può seguire la procedura descritta nella pagina dedicata <Link to="/reclami" className="text-[#163321] underline">Gestione Reclami</Link>, conforme all'art. 10 del Regolamento IVASS n. 24/2008.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">11. Legge applicabile e foro competente</h2>
              <p>
                I presenti Termini sono regolati dalla <strong>legge italiana</strong>. Per ogni controversia relativa all'interpretazione, esecuzione o risoluzione dei Termini è competente in via esclusiva il Foro di <strong>Napoli</strong>, fatta salva la competenza inderogabile del Foro del Consumatore ai sensi dell'art. 33 co. 2 lett. u) del D.Lgs. 206/2005 (Codice del Consumo) laddove applicabile.
              </p>
              <p>
                Prima di adire l'autorità giudiziaria, l'Utente può avvalersi della piattaforma europea di risoluzione alternativa delle controversie online (<strong>ODR</strong>) raggiungibile all'indirizzo: <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">ec.europa.eu/consumers/odr</a>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">12. Modifiche</h2>
              <p>
                Colucci Assicuratori si riserva il diritto di modificare i presenti Termini in qualsiasi momento. Le modifiche saranno efficaci dal momento della loro pubblicazione sul sito. L'Utente è invitato a consultare periodicamente questa pagina.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">13. Contatti</h2>
              <p>
                Per informazioni sui presenti Termini:<br />
                <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong><br />
                Via Armando Diaz 182/L — 80055 Portici (NA)<br />
                Email: <a href="mailto:info@colucciassicuratori.com" className="text-[#163321] underline">info@colucciassicuratori.com</a><br />
                PEC: <a href="mailto:coluccisas@pec.it" className="text-[#163321] underline">coluccisas@pec.it</a><br />
                Telefono: <a href="tel:0817611000" className="text-[#163321] underline">081 761 1000</a>
              </p>
            </section>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}
